<template>
  <div
    data-name="ResponsibilityCard"
    :class="[showInspectionForm ? '' : 'responsibility-card-wrapper']"
  >
    <!-- When inspecting: hide header and card content, show only InspectToAwardForm (like EmployeeDetail) -->
    <template v-if="!showInspectionForm">
      <div class="responsibility-card-header-wrapper">
        <ResponsibilityCardHeader
          :breadcrumbs="computedBreadcrumbs"
          :status="responsibilityDetails?.status || ''"
          :code="getOrgCodeName(responsibilityDetails) || ''"
          :name="responsibilityDetails?.name || ''"
          :archived="responsibilityDetails?.archived || false"
          :avg_readiness="responsibilityDetails?.avg_readiness || 0"
          :dataStream="chatterList"
          :totalChatterCount="totalChatterCount"
          :currentResponsibility="responsibilityDetails"
          :chatterDrawerOpen="chatterDrawerOpen"
          :show_menu="effectiveShowHeaderMenu"
          @update:chatterDrawerOpen="chatterDrawerOpen = $event"
          @reloadChatterList="handleReloadChatterList"
          @requestMoreCheckpointsChatter="handleRequestMoreChatter"
          @optionClick="handleOptionClick"
          @backToList="handleBreadcrumbNavigation"
        />
        <ResponsibilityCardMenu
          class="responsibility-card-menu"
          :checklist_count="checklist_count"
          :badge_holders_count="badge_holders_count"
          :assignments_count="assignments_count"
          :attached_roles_count="attachedRolesMenuCount"
          :has_context="showContextTabV2 ? hasContext : hasPurpose"
          :purpose-details-loaded="responsibilityDetailsLoaded"
          :show_context_count_badge="showContextCountBadge"
          :context_files_count="contextFilesCount"
          :show-attached-roles-tab="showAttachedRolesLd"
          :show-context-tab="showContextTabLd"
          @tab-change="handleTabChange"
          :selectedTab="selectedTab"
        />
      </div>

      <div class="responsibility-card-content">
        <EditCheckpointsTab
          v-if="selectedTab === 0"
          ref="editCheckpointsTabRef"
          :responsibilityId="computedResponsibilityId"
          :responsibilityCheckpointCounts="responsibilityCheckpointCounts"
          :responsibilityNameProp="responsibilityDetails?.name || ''"
          :responsibilityCodeProp="getOrgCodeName(responsibilityDetails)"
          :responsibilityPurposeProp="responsibilityDetails?.purpose || ''"
          :allowDragCheckpointToTree="allowDragCheckpointToTree"
          :on-checkpoint-drag-start-callback="onCheckpointDragStartCallback"
          :on-checkpoint-drag-end-callback="onCheckpointDragEndCallback"
          @checkpointCountChanged="handleCheckpointCountChanged"
          @drilldown="handleDrilldown"
        />
        <ResponsibilityBadgeHoldersTab
          v-if="selectedTab === 1"
          ref="badgeHoldersTabRef"
          :responsibility-id="computedResponsibilityId"
          :responsibilityData="responsibilityDetails"
          :position-name="inspectionContextPositionName"
          :role-name="inspectionContextRoleName"
          :position-code="inspectionContextPositionCode"
          :role-code="inspectionContextRoleCode"
          @badgeCountsUpdated="handleBadgeCountsUpdated"
          @inspectionOpen="handleInspectionOpen"
          @inspectionClose="showInspectionForm = false"
          :filter-levels="badgeHolderFilterLevels"
        />
        <ResponsibilityAssignmentsTab
          v-if="selectedTab === 2"
          :current-response="assignmentsCurrentResponse"
          :data-stream="assignmentsDataStream"
          :responsibility-id="computedResponsibilityId"
          :account-id="accountIdComputed"
          :avg_readiness="responsibilityDetails?.avg_readiness || 0"
          @weight-changed="handleWeightChanged"
          @badge-counts-updated="handleBadgeCountsUpdated"
        />
        <ResponsibilityAttachedRolesTab
          v-if="isAttachedRolesTabActive"
          :key="computedResponsibilityId"
          :responsibility-id="computedResponsibilityId"
          :responsibility-name="responsibilityDetails?.name || ''"
          :responsibility-archived="Boolean(responsibilityDetails?.archived)"
          :drilldown-in-panel="drilldownInPanel"
          @responsibility-data-updated="handleAttachedRolesDataUpdated"
          @attached-roles-count-updated="handleAttachedRolesCountUpdated"
          @drilldown="handleDrilldown"
        />
        <!--
          ER-9892 / MDP-494: behind the idea-MDP-494 flag, render the new ContextTab
          (ContextFiles + ContextEditor); otherwise keep the legacy ResponsibilityPurposeTab.
        -->
        <ContextTab
          v-if="isPurposeTabActive && showContextTabV2"
          :state="contextState"
          :context="responsibilityDetails?.context || null"
          :context-updated-at="responsibilityDetails?.context_updated_at || null"
          :context-updated-by="responsibilityDetails?.context_updated_by || null"
          :files="contextFiles"
          :uploading-files="uploadingContextFiles"
          :disabled="isSavingContext"
          :draft="contextDraft"
          :max-length="ENTITY_CONTEXT_MAX_LENGTH"
          :suggesting="isSuggestingContext"
          :suggest-error="contextSuggestError"
          @save="handleContextSave($event)"
          @suggest="handleContextSuggest"
          @improve="handleContextImprove"
          @input="setContextDraft"
          @discard="clearContextDraft"
          @upload="handleContextUpload"
          @remove="handleContextRemove"
        />
        <ResponsibilityPurposeTab
          v-else-if="isPurposeTabActive"
          :value="responsibilityDetails?.purpose || ''"
          :hideAIButtons="true"
          :isSaving="isSavingPurpose"
          @save="handlePurposeSave($event)"
          :maxLength="1000"
        />
        <ResponsibilityDefaultsTab
          v-if="isDefaultsTabActive"
          :responsibilityId="computedResponsibilityId"
          @createDefaultBadge="handleCreateDefaultBadge"
        />
      </div>
    </template>

    <!-- InspectToAwardForm: full page when inspection open (same as EmployeeDetail) -->
    <div
      v-if="showInspectionForm && inspectionFormattedResponsibility"
      class="inspect-form-page-wrapper"
    >
      <InspectToAwardForm
        :breadcrumbsList="computedBreadcrumbs"
        :employee="inspectionFormattedEmployee"
        :responsibility="inspectionFormattedResponsibility"
        :inspectionData="inspectionData"
        :filterText="inspectionFilterText"
        :showNoRecords="showNoInspectionRecords"
        :showPagination="showInspectionPagination"
        :pagination="inspectionPagination"
        :chipText="inspectionChipText"
        :filterGroups="inspectionFilterGroups"
        :filterType="inspectionFilterType"
        :statusOptions="inspectionStatusOptions"
        :groupsItems="inspectionGroupsItems"
        :levelCounter="inspectionLevelCounter"
        :currentBadge="inspectionCurrentBadgeLevel"
        :readyForLevel="inspectionReadyForBadgeLevel"
        :dot="inspectionDot"
        @onClickBreadcrumb="handleInspectionBreadcrumbClick"
        @badge-selected="handleInspectionBadgeSelected"
        @onSearch="handleInspectionSearch"
        @onClick="handleInspectionClick"
        @removeFilter="handleInspectionRemoveFilter"
        @filterTypes="handleInspectionFilterTypes"
        @onPaginationRequest="handleInspectionPagination"
        @clearSelection="handleInspectionClearSelection"
        @onCheckedItemsChanged="handleInspectionCheckedItemsChanged"
        @onPass="handleInspectionPass"
        @onFail="handleInspectionFail"
        @onNotApplicable="handleInspectionNotApplicable"
        @onClose="(row) => handleInspectionResetOrClose(row)"
      />
    </div>

    <!-- Inspection: Reason Modal (Fail / Not Applicable) -->
    <ReasonModal
      v-if="inspectionReasonModal.show"
      :showDialog="inspectionReasonModal.show"
      :title="inspectionReasonModal.config.title"
      :description="inspectionReasonModal.config.description"
      :placeHolder="inspectionReasonModal.config.placeHolder"
      :btnLabel="inspectionReasonModal.config.btnLabel"
      :btnDanger="inspectionReasonModal.config.btnDanger"
      :btnColor="inspectionReasonModal.config.btnColor"
      :value="inspectionReason"
      @input="(val) => (inspectionReason = val)"
      @onAction="handleInspectionReasonAction"
      @onClose="closeInspectionReasonModal"
    />
    <!-- Inspection: Reset Evaluation Confirmation -->
    <ConfirmationDialog
      :dialog="showInspectionResetConfirm"
      @confirm="handleConfirmResetInspectionEvaluation"
      @cancel="showInspectionResetConfirm = false"
      :icon="'alert-icon-red.svg'"
    >
      <template v-slot:title>Reset Evaluation</template>
      <template v-slot:content> Are you sure you want to reset this evaluation? </template>
    </ConfirmationDialog>
    <!-- Inspection: Remove Badge Dialog -->
    <RemoveBadgeDialog
      v-model="showInspectionRemoveBadgeDialog"
      :badgeName="
        selectedBadgeForInspectionRemoval?.badgeName ||
        selectedBadgeForInspectionRemoval?.level ||
        ''
      "
      :responsibilityName="selectedBadgeForInspectionRemoval?.responsibilityName || ''"
      :employeeName="selectedBadgeForInspectionRemoval?.employeeName || ''"
      @remove="handleConfirmInspectionRemoveBadge"
      @close="handleCloseInspectionRemoveBadgeDialog"
    />
    <!-- Inspection: Award Badge Dialog -->
    <AwardBadgeDialog
      :isVisible="showInspectionAwardBadgeDialog"
      :employee="inspectionAwardBadgeEmployee"
      :level="inspectionAwardBadgeLevel"
      :currentEmployee="inspectionAwardBadgeDialogCurrentEmployee"
      :eligibility-by-access-null="true"
      :responsibilityName="
        inspectionFormattedResponsibility?.responsibilityName || responsibilityDetails?.name || ''
      "
      @close="closeInspectionAwardBadgeDialog"
      @skip="skipInspectionAwardBadge"
      @activate="activateInspectionAwardBadge"
    />
    <GiveBadgeDialog
      :value="showInspectionGiveBadgeDialog"
      :description="inspectionGiveBadgeDescription"
      :employeeName="inspectionGiveBadgeEmployeeName"
      :responsibilityName="
        inspectionFormattedResponsibility?.responsibilityName || responsibilityDetails?.name || ''
      "
      :canNotify="inspectionGiveBadgeCanNotify"
      :defaultLevel="inspectionGiveBadgeDefaultLevel"
      :currentBadgeLevel="inspectionGiveBadgeCurrentBadgeLevel"
      :lockedLevel="inspectionGiveBadgeLockedLevel"
      @input="onInspectionGiveBadgeDialogInput"
      @give-badge="handleInspectionGiveBadgeConfirm"
      @close="closeInspectionGiveBadgeDialog"
    />

    <!-- Dialog Components -->
    <RenameResponsibilityDialog
      v-if="showRenameDialog && renameResponsibilityModel"
      v-model="showRenameDialog"
      :responsibility="renameResponsibilityModel"
      @saved="handleResponsibilityRenamed"
    />
    <DeleteResponsibility
      v-model="showDeleteDialog"
      :responsibility="responsibilityName"
      @onDelete="handleDeleteResponsibility"
    />
    <ActivateResponsibility
      v-model="showActivateDialog"
      :responsibility="responsibilityName"
      :employee-count="activationEmployeeCount"
      @onSave="handleActivateResponsibility"
    />
    <RetireResponsibility
      v-model="showRetireDialog"
      :responsibility="responsibilityName"
      @onSave="handleRetireResponsibility"
    />
    <ReActivateResponsibility
      v-model="showReActivateDialog"
      :responsibility="responsibilityName"
      :employee-count="activationEmployeeCount"
      @onSave="handleReActivateResponsibility"
    />
    <ArchivePopUp
      :action="archiveAction"
      type="responsibility"
      :show="showArchiveDialog"
      :curretObj="currentOrgResponsibility"
      :errorMessage="unarchiveError"
      @onClose="onArchiveDialogClose"
      @onArchive="handleArchiveResponsibility"
    />
    <UnarchiveNameConflictDialog
      :show="unarchiveConflictOpen"
      objectType="responsibility"
      :currentName="unarchiveTarget?.name || ''"
      :objectId="unarchiveTarget?.id || ''"
      :accountId="accountIdComputed"
      :busy="unarchiveBusy"
      :errorMessage="unarchiveError"
      :nameError="unarchiveNameError"
      @confirm="unarchiveResponsibility.submitRename"
      @close="onArchiveDialogClose"
      @clear-error="unarchiveResponsibility.clearErrors"
    />
    <AttachedResponsibilityToRole
      v-model="showAttachRoleDialog"
      OrgObjectType="Responsibility to Role"
      @onSave="handleAttachToRole"
      :type="showAttachResponsibilitiesPicker ? 'respToRole_new_picker' : 'respToRole'"
      :items="attachableRoles"
      @item-selected="handleRoleSelected"
      @add-item-selected="handleAddNewRole"
      :parent-name="responsibilityName"
      :hasMoreData="hasMoreData"
      :isLoadingMore="isLoadingMore"
      @load-more="loadMoreRoles"
      @search="searchRoles"
      :isAssigning="isAssigningRole"
    />
    <ContextIQOverlay
      v-if="contextIQOverlayEnabled"
      :model-value="isContextIQOverlayOpen"
      object-type="responsibility"
      :object-id="computedResponsibilityId"
      :object-name="responsibilityName"
      :context="contextOverlaySeed"
      @save="handleContextOverlaySave"
      @close="isContextIQOverlayOpen = false"
    />
  </div>
</template>

<script setup>
import { normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";
import { ref, computed, onMounted, onUnmounted, watch, getCurrentInstance, nextTick } from "vue";
import { useIQOverlayStore } from "@/store/pinia/iqOverlay";
import { fetchResponsibilityActivationCount } from "@/utils/activationEmployeeCount";
import ResponsibilityCardMenu from "@components/ResponsibilityCardMenu/ResponsibilityCardMenu.vue";
import ResponsibilityPurposeTab from "@components/ResponsibilityPurposeTab/ResponsibilityPurposeTab.vue";
import ContextTab from "@components/ContextTab/ContextTab.vue";
import ContextIQOverlay from "@components/ContextIQOverlay/ContextIQOverlay.vue";
import {
  useContextSuggestion,
  ENTITY_CONTEXT_MAX_LENGTH,
} from "@components/utils/useContextSuggestion";
import EditCheckpointsTab from "@components/EditCheckpointsTab/EditCheckpointsTab.vue";
import ResponsibilityBadgeHoldersTab from "@components/ResponsibilityBadgeHoldersTab/ResponsibilityBadgeHoldersTab.vue";
import ResponsibilityAssignmentsTab from "@components/ResponsibilityAssignmentsTab/ResponsibilityAssignmentsTab.vue";
import ResponsibilityDefaultsTab from "@components/ResponsibilityDefaultsTab/ResponsibilityDefaultsTab.vue";
import ResponsibilityAttachedRolesTab from "@components/ResponsibilityAttachedRolesTab/ResponsibilityAttachedRolesTab.vue";
import ResponsibilityCardHeader from "@components/ResponsibilityCardHeader/ResponsibilityCardHeader.vue";
// Dialog Components
import RenameResponsibilityDialog from "@components/RolesCatalog/RenameResponsibilityDialog/RenameResponsibilityDialog.vue";
import { toRenameResponsibilityModel } from "@components/utils/renameResponsibilityModel";
import DeleteResponsibility from "@components/RolesCatalog/DeleteResponsibility.vue";
import ActivateResponsibility from "@components/RolesCatalog/ActivateResponsibility.vue";
import RetireResponsibility from "@components/RolesCatalog/RetireResponsibility.vue";
import ReActivateResponsibility from "@components/RolesCatalog/ReActivateResponsibility.vue";
import ArchivePopUp from "@components/common/ArchivePopUp.vue";
import UnarchiveNameConflictDialog from "@components/UnarchiveNameConflictDialog/UnarchiveNameConflictDialog.vue";
import { useUnarchiveOrgObject } from "@/composables/useUnarchiveOrgObject";
import AttachedResponsibilityToRole from "@components/RolesCatalog/AttachedResponsibilityToRole.vue";
import InspectToAwardForm from "@components/InspectToAwardForm/InspectToAwardForm.vue";
import ReasonModal from "@components/ReasonModal/ReasonModal.vue";
import ConfirmationDialog from "@components/ResponsibilitiesPicker/ConfirmationDialog.vue";
import AwardBadgeDialog from "@components/AwardBadgeDialog/AwardBadgeDialog.vue";
import RemoveBadgeDialog from "@components/RemoveBadgeDialog/RemoveBadgeDialog.vue";
import GiveBadgeDialog from "@components/GiveBadgeDialog/GiveBadgeDialog.vue";
// API imports
import {
  getResponsibilityIdV2,
  getResponsibilityChatterV2,
  deleteResponsibility,
  updateActivateResponsibility,
  updateRetireResponsibility,
  updateReActivateResponsibility,
  updateArchiveResponsibility,
  attachResponsibilityToRole,
  fetchAttachableRoles,
  updateResponsibilityPurpose,
  updateResponsibilityContext,
  getContextFiles,
  uploadContextFiles,
  deleteContextFile,
  mapContextFileToUi,
  getListActiveCheckpointsEvalV2,
  getResponsibilityChecklistV2,
  passCheckPointEvaluation,
  failCheckPointEvaluation,
  notApplicableCheckPointEvaluation,
  resetCheckPointEvaluation,
  createProficiencyBadge,
  getEmployeeBadges,
  deleteResponsibilityBadge,
  getAssignmentEval,
  getListOfRolesAttachedToResponsibility,
} from "@/api";
import launchDarklyService from "@/services/launchdarkly";
import {
  LD_FLAG_CONTEXT_TAB_RESPONSIBILITY_PAGE,
  LD_FLAG_IDEA_MDP_494,
  LD_FLAG_IDEA_MDP_523,
} from "@components/constants/launchDarklyFlagKeys";
import { getDeleteErrorMessage } from "@components/utils/getDeleteErrorMessage";
import {
  buildResponsibilityTabNameToIndex,
  buildResponsibilityTabIndexToName,
  normalizeResponsibilityRouteTabParam,
  resolveResponsibilityTabFromRoute,
} from "@components/utils/responsibilityCardTabs";
import {
  pickBadgeForResponsibility,
  isLevelCompleteForAward,
} from "@/utils/inspectionBadgeSync";
import { proficiencyCheckpointErrorMessage } from "@/utils/proficiencyBadgeHelpers";
import { BADGE_LEVEL_DISPLAY_LABELS, getBadgeLevelLabel } from "@components/utils/badgeLevelLabels";
import { resolveAppointedDot } from "@components/utils/appointedBadge";
import {
  ACTIVE_ATTACHED_ROLES_FILTERS,
  resolveActiveAttachedRolesCount,
} from "@components/utils/activeAttachedRolesCount";
const props = defineProps({
  // Purpose Tab Props
  purposeValue: {
    type: String,
    default: "",
  },
  purposePlaceholder: {
    type: String,
    default: "Enter purpose for this responsibility...",
  },
  purposeMaxLength: {
    type: Number,
    default: 500,
  },
  purposeIsSaving: {
    type: Boolean,
    default: false,
  },
  // Badge Holders Tab Props
  badgeHoldersResponsibilityId: {
    type: [Number, String],
    default: 1,
  },
  // Assignments Tab Props
  assignmentsCurrentResponse: {
    type: Object,
    default: () => ({}),
  },
  assignmentsDataStream: {
    type: Array,
    default: () => [],
  },
  status: {
    type: String,
    default: "",
  },
  code: {
    type: String,
    default: "",
  },
  name: {
    type: String,
    default: "",
  },
  avgReadiness: {
    type: Number,
    default: 0,
  },
  // Optional resId prop - if provided, uses this instead of route params
  resId: {
    type: [Number, String],
    default: null,
  },
  /** When true, checkpoints can be dragged to the positions tree to move to another responsibility. */
  allowDragCheckpointToTree: {
    type: Boolean,
    default: false,
  },
  onCheckpointDragStartCallback: { type: Function, default: null },
  onCheckpointDragEndCallback: { type: Function, default: null },
  /** When false, hides the action menu (kebab) in the responsibility card header. */
  showHeaderMenu: {
    type: Boolean,
    default: true,
  },
  /** When true (e.g. Positions Tree View), breadcrumb drilldown stays in same panel */
  drilldownInPanel: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "tab-change",
  "purpose-input",
  "purpose-save",
  "purpose-discard",
  "add-purpose",
  "purpose-edit",
  "purpose-improve",
  "purpose-suggest",
  "weight-changed",
  "create-default-badge",
  "drilldown",
  "responsibilityDataUpdated",
  "close-embedded",
  "item-deleted",
]);

const selectedTab = ref(0);
/**
 * ER-10461: latest tab name requested by the user. Route-watch updates that do not match
 * this (stale in-flight replaces) are ignored so content stays in sync with the click.
 */
const pendingResTab = ref(null);
/** idea-MDP-464 (LaunchDarkly client-side): show "Attachment to Roles" tab when true. Starts true to match LD default until variation loads. */
const showAttachedRolesLd = ref(true);
/** context-tab-responsibility-page: show Context tab when true. Starts true until LD variation loads. */
const showContextTabLd = ref(true);
/** idea-MDP-494: render the new ContextTab instead of the legacy purpose tab. Starts false so the old UI shows until LD resolves. */
const showContextTabV2 = ref(false);
/** idea-MDP-523: Context tab numeric count badge (files + context text). Starts false until LD resolves. */
const showContextCountBadge = ref(false);
const editCheckpointsTabRef = ref(null);
const showInspectionForm = ref(false);
const badgeHoldersTabRef = ref(null);
const responsibilityDetails = ref(null);
/** When false, do not show the Context + badge (avoids flashing + before API returns purpose). */
const responsibilityDetailsLoaded = ref(false);

// Inspection form state (same pattern as EmployeeDetail)
const inspectionData = ref([]);
const inspectionFilterText = ref("");
const showNoInspectionRecords = ref(false);
const showInspectionPagination = ref(false);
const inspectionPagination = ref({ total: 0, page: 1, perPage: 10 });
const inspectionChipText = ref([]);
const inspectionFilterGroups = ref([]);
const inspectionFilterType = ref("Checkpoint");
const inspectionStatusOptions = ref([
  "Not Yet Passed",
  "Only Passed",
  "Only Failed",
  "Only Not Yet Evaluated",
  "Only Not Applicable",
  "Only Reset",
]);
const inspectionGroupsItems = ref([
  {
    name: "Evaluation Status",
    groups: [
      {
        type: "check",
        label: "Status",
        items: [
          { label: "Not Yet Passed", value: "Not Yet Passed" },
          { label: "Only Passed", value: "Only Passed" },
          { label: "Only Failed", value: "Only Failed" },
          { label: "Only Not Yet Evaluated", value: "Only Not Yet Evaluated" },
          { label: "Only Not Applicable", value: "Only Not Applicable" },
          { label: "Only Reset", value: "Only Reset" },
        ],
      },
    ],
  },
]);
const inspectionLevelCounter = ref({
  Apprentice: 0,
  Professional: 0,
  Coach: 0,
  Master: 0,
});
const inspectionCurrentBadgeLevel = ref(null);
const inspectionDot = ref("Good Standing");
const inspectionFormattedResponsibility = ref(null);
const inspectionFormattedEmployee = ref(null);
const inspectionEmployeeId = ref(null);
/** Employee `access` for AwardBadgeDialog notify rules (Path to Readiness parity). */
const inspectionEmployeeAccess = ref(null);
const inspectionReasonModal = ref({ show: false, type: null, config: {} });
const inspectionReasonModalConfigs = {
  fail: {
    title: "Add Criteria Failed Reason",
    description: "Please provide a reason why this criteria was not met.",
    placeHolder: "Explain why the criteria was not fulfilled or provide specific feedback.",
    btnLabel: "Fail",
    btnDanger: true,
    btnColor: null,
  },
  notApplicable: {
    title: "Mark Criteria as Not Applicable",
    description: "Provide a reason why this criteria is not applicable.",
    placeHolder: "Explain why the criteria is not applicable.",
    btnLabel: "Mark as Not Applicable",
    btnDanger: false,
    btnColor: "#666666",
  },
};
const inspectionReason = ref("");
const inspectionSelectedCheckpoint = ref(null);
const showInspectionResetConfirm = ref(false);
const inspectionCheckpointToReset = ref(null);
const showInspectionAwardBadgeDialog = ref(false);
const inspectionAwardBadgeLevel = ref("");
const inspectionCurrentBadgeData = ref(null);
const showInspectionRemoveBadgeDialog = ref(false);
const selectedBadgeForInspectionRemoval = ref(null);
const inspectionReadyForBadgeLevel = computed(() => {
  const data = inspectionData.value || [];
  const checkpointsByLevel = { Apprentice: [], Professional: [], Coach: [], Master: [] };
  data.forEach((cp) => {
    if (checkpointsByLevel[cp.level]) checkpointsByLevel[cp.level].push(cp);
  });
  const levels = ["Master", "Coach", "Professional", "Apprentice"];
  for (const level of levels) {
    const checkpoints = checkpointsByLevel[level];
    if (!checkpoints?.length) continue;
    if (
      checkpoints.every(
        (c) => c.evaluation_status === "passed" || c.evaluation_status === "not_applicable"
      )
    )
      return level;
  }
  return "Novice";
});
const inspectionAwardBadgeEmployee = computed(() => ({
  name:
    [inspectionFormattedEmployee.value?.firstName, inspectionFormattedEmployee.value?.lastName]
      .filter(Boolean)
      .join(" ") || "Employee",
  avatar: inspectionFormattedEmployee.value?.photoUrl || "",
}));

const inspectionAwardBadgeDialogCurrentEmployee = computed(() => ({
  user: inspectionEmployeeId.value,
  access: inspectionEmployeeAccess.value ?? null,
}));

const showInspectionGiveBadgeDialog = ref(false);
const inspectionGiveBadgeLockedLevel = ref(null);
const inspectionGiveBadgeDefaultLevel = ref("apprentice");

const inspectionGiveBadgeEmployeeName = computed(
  () =>
    [inspectionFormattedEmployee.value?.firstName, inspectionFormattedEmployee.value?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() || "Employee"
);

const inspectionGiveBadgeDescription = computed(() => {
  const respName =
    inspectionFormattedResponsibility.value?.responsibilityName ||
    responsibilityDetails.value?.name ||
    "this responsibility";
  const empName = inspectionGiveBadgeEmployeeName.value;
  return `Responsibility ${respName} has no Readiness Checklist therefore you can only Give (Instead of Awarding) a Badge to employee ${empName}.`;
});

const inspectionGiveBadgeCanNotify = computed(() => {
  const a = inspectionEmployeeAccess.value;
  if (a == null) return false;
  if (a.is_suspended === true) return false;
  if (a.has_access === false) return false;
  return true;
});

const inspectionGiveBadgeCurrentBadgeLevel = computed(() => {
  const raw = inspectionCurrentBadgeData.value?.level;
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && raw >= 1 && raw <= 4) {
    return ["apprentice", "professional", "coach", "master"][raw - 1];
  }
  const s = String(raw).trim().toLowerCase();
  if (["apprentice", "professional", "coach", "master"].includes(s)) return s;
  return null;
});
const checklist_count = ref(0);
const badge_holders_count = ref(0);
const assignments_count = ref(0);
const attachedRolesMenuCount = ref(0);
const isSavingPurpose = ref(false);
/** idea-MDP-523 (ER-10140): Suggest / Improve open the IQ Overlay instead of the inline draft. */
const contextIQOverlayEnabled = ref(false);
const isContextIQOverlayOpen = ref(false);
/** idea-MDP-494 ContextTab save-in-flight flag. */
const isSavingContext = ref(false);
/** ER-10218: IQ "Suggest" for the Context field — the suggestion is an unsaved draft. */
const {
  draft: contextDraft,
  isSuggesting: isSuggestingContext,
  suggestError: contextSuggestError,
  suggest: suggestContextText,
  setDraft: setContextDraft,
  clearDraft: clearContextDraft,
} = useContextSuggestion("responsibility");
/** Uploaded context files for this responsibility (ER-10294 context-files API). */
const contextFiles = ref([]);
/** Files mid-upload, rendered as progress-bar cards until the list refresh replaces them. */
const uploadingContextFiles = ref([]);

// Chatter data
const chatterList = ref([]);
const totalChatterCount = ref(0);
const chatterDrawerOpen = ref(false);
const loadedChatterPages = ref(new Set());

// Dialog visibility refs
const showRenameDialog = ref(false);
const renameResponsibilityModel = computed(() =>
  computedResponsibilityId.value
    ? toRenameResponsibilityModel({
        id: computedResponsibilityId.value,
        name: responsibilityName.value,
        context: responsibilityDetails.value?.context,
        status: responsibilityDetails.value?.status,
        archived: responsibilityDetails.value?.archived,
      })
    : null
);
const showDeleteDialog = ref(false);
const showActivateDialog = ref(false);
const showRetireDialog = ref(false);
const showReActivateDialog = ref(false);
const activationEmployeeCount = ref(0);
const showArchiveDialog = ref(false);
const showAttachRoleDialog = ref(false);
const archiveAction = ref(null);
const attachableRoles = ref([]);
const selectedRoleId = ref(null);
const isAssigningRole = ref(false);

// Pagination refs for attach role dialog
const hasMoreData = ref(false);
const isLoadingMore = ref(false);
const currentPage = ref(1);
const currentSearchTerm = ref("");
const totalCount = ref(0);
let unsubscribeCheckpointCreated = null;

// Computed property for archive dialog
const currentOrgResponsibility = computed(() => {
  if (!responsibilityDetails.value) return null;
  return {
    realId: responsibilityDetails.value.id,
    data: responsibilityDetails.value,
  };
});

const showAttachResponsibilitiesPicker = computed(() => {
  return proxy.$store.getters["featureFlags/ideaMDP389"];
});

// Computed property for responsibility name (to avoid optional chaining in template)
const responsibilityName = computed(() => {
  return responsibilityDetails.value?.name || "";
});

const instance = getCurrentInstance();
const proxy = instance?.proxy;

// Inspection context from route (for position/role name in InspectToAwardForm)
const inspectionContextPositionName = computed(() => proxy?.$route?.query?.positionName || "");
const inspectionContextRoleName = computed(() => proxy?.$route?.query?.roleName || "");
const inspectionContextPositionCode = computed(() => proxy?.$route?.query?.positionCode || "");
const inspectionContextRoleCode = computed(() => proxy?.$route?.query?.roleCode || "");

/** When true, user came from As an Architect (Your Stuff) — breadcrumb goes back to As an Architect. */
const isFromAsMaster = computed(() => proxy?.$route?.query?.from === "as-master");
// Header menu visibility follows the caller-supplied permission, not navigation origin —
// this route already requires the user to be a badge/assignment holder for as-master access.
const effectiveShowHeaderMenu = computed(() => props.showHeaderMenu);

const computedBreadcrumbs = computed(() => {
  const route = proxy?.$route;
  const useQuery = props.drilldownInPanel || (route?.query?.positionId && route?.query?.roleId);
  // In tree view (Pane) we use query; otherwise params
  const positionId = useQuery ? route?.query?.positionId : route?.params?.positionId;
  const positionName = route?.query?.positionName;
  const roleId = useQuery ? route?.query?.roleId : route?.params?.roleId;
  const roleName = route?.query?.roleName;
  const roleCode = route?.query?.roleCode;

  let baseCrumbs = [];

  // Case 0: As A Master (Your Stuff) — takes priority over position/role context
  if (isFromAsMaster.value) {
    baseCrumbs = [
      { text: "As an Architect", href: "", disabled: false },
      { text: "Responsibility", href: "", disabled: true },
    ];
  }
  // Case 1: Position → Role → Responsibility (tree view or position detail)
  else if (positionId && roleId) {
    const roleDisplayText = roleName ? (roleCode ? `${roleCode} ${roleName}` : roleName) : "Role";
    baseCrumbs = [
      {
        text: positionName || "Position",
        href: "",
        disabled: false,
        icon: "org_icon_position",
        positionId: positionId,
      },
      { text: roleDisplayText, href: "", disabled: false, icon: "org_icon_role", roleId: roleId },
      { text: "Responsibility", href: "", disabled: true },
    ];
  }
  // Case 2: Role → Responsibility (no position context)
  else if (roleId && (roleName || useQuery)) {
    const roleDisplayText = roleName ? (roleCode ? `${roleCode} ${roleName}` : roleName) : "Role";
    baseCrumbs = [
      { text: "Roles", href: "", disabled: false },
      { text: roleDisplayText, href: "", disabled: false, icon: "org_icon_role", roleId: roleId },
      { text: "Responsibility", href: "", disabled: true },
    ];
  }
  // Case 3: Standalone Responsibility
  else {
    baseCrumbs = [
      { text: "Responsibilities", href: "", disabled: false },
      { text: "Responsibility", href: "", disabled: true },
    ];
  }

  // When inspection form is shown, show only Badge Holders + Readiness Inspection (hide base crumbs)
  if (showInspectionForm.value) {
    return [
      { text: "Badge Holders", href: "", disabled: false },
      { text: "Readiness Inspection", href: "", disabled: true },
    ];
  }
  return baseCrumbs;
});

const tabNameToIndex = computed(() =>
  buildResponsibilityTabNameToIndex(showAttachedRolesLd.value, showContextTabLd.value)
);

const tabIndexToName = computed(() => buildResponsibilityTabIndexToName(tabNameToIndex.value));

const isAttachedRolesTabActive = computed(
  () => showAttachedRolesLd.value && selectedTab.value === 3
);
const purposeTabIndex = computed(() => tabNameToIndex.value.purpose);
const defaultsTabIndex = computed(() => tabNameToIndex.value.defaults);

const isPurposeTabActive = computed(
  () =>
    showContextTabLd.value &&
    purposeTabIndex.value !== undefined &&
    selectedTab.value === purposeTabIndex.value
);
const isDefaultsTabActive = computed(
  () =>
    defaultsTabIndex.value !== undefined && selectedTab.value === defaultsTabIndex.value
);

function replaceResTabQuery(tabName) {
  // Embedded in a position pane: keep tab state local so query updates do not
  // remount the parent PositionForm / attached-roles tree.
  if (props.drilldownInPanel) return;
  const route = proxy?.$route;
  if (!proxy?.$router || !route) return;
  const query = { ...route.query, resTab: tabName };
  delete query.tab;
  proxy.$router.replace({ path: route.path, query }).catch(() => {});
}

async function loadTabFeatureFlags() {
  try {
    const [attachedRoles, contextTab, contextV2, contextCountBadge] = await Promise.all([
      launchDarklyService.getFlag("idea-MDP-464", true),
      launchDarklyService.getFlag(LD_FLAG_CONTEXT_TAB_RESPONSIBILITY_PAGE, true),
      launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_494, false),
      launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_523, false),
    ]);
    showAttachedRolesLd.value = attachedRoles === true;
    showContextTabLd.value = contextTab === true;
    showContextTabV2.value = contextV2 === true;
    showContextCountBadge.value = contextCountBadge === true;
    contextIQOverlayEnabled.value = contextCountBadge === true;
  } catch {
    showAttachedRolesLd.value = true;
    showContextTabLd.value = true;
    showContextTabV2.value = false;
    showContextCountBadge.value = false;
    contextIQOverlayEnabled.value = false;
  }
}

function applyRouteTabParam(tabParam) {
  const normalized = normalizeResponsibilityRouteTabParam(tabParam);
  if (pendingResTab.value && normalized !== pendingResTab.value) {
    // Stale route update from an earlier replace — keep optimistic selection
    return;
  }
  const { index, redirectTo } = resolveResponsibilityTabFromRoute(
    tabParam,
    showAttachedRolesLd.value,
    showContextTabLd.value
  );
  selectedTab.value = index;
  if (redirectTo) {
    pendingResTab.value = redirectTo;
    replaceResTabQuery(redirectTo);
  } else if (normalized === pendingResTab.value) {
    pendingResTab.value = null;
  }
}

function syncSelectedTabAfterMenuStructureChange() {
  if (!showContextTabLd.value) {
    const routeTab = normalizeResponsibilityRouteTabParam(
      proxy?.$route?.query?.resTab || proxy?.$route?.query?.tab
    );
    if (routeTab === "purpose") {
      selectedTab.value = tabNameToIndex.value.checklist ?? 0;
      replaceResTabQuery("checklist");
      return;
    }
  }

  const tabName = tabIndexToName.value[selectedTab.value];
  if (!tabName) {
    selectedTab.value = tabNameToIndex.value.checklist ?? 0;
    replaceResTabQuery("checklist");
    return;
  }
  if (tabName === "attached-roles" && !showAttachedRolesLd.value) {
    selectedTab.value = tabNameToIndex.value.checklist ?? 0;
    replaceResTabQuery("checklist");
    return;
  }
  if (tabName === "purpose" && !showContextTabLd.value) {
    selectedTab.value = tabNameToIndex.value.checklist ?? 0;
    replaceResTabQuery("checklist");
    return;
  }
  const idx = tabNameToIndex.value[tabName];
  if (idx !== undefined && idx !== selectedTab.value) {
    selectedTab.value = idx;
  }
}

// Set initial tab from route query (check both 'tab' and 'resTab')
onMounted(async () => {
  await loadTabFeatureFlags();

  const route = proxy?.$route;
  const tabParam = route?.query?.resTab || route?.query?.tab;
  applyRouteTabParam(tabParam);

  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  // Only fetch if we have a valid resId
  if (!resId) {
    console.warn("ResponsibilityCard mounted without resId");
    return;
  }

  await loadResponsibilityDetails(accountId, resId);

  // Subscribe to checkpoint-created from IQ overlay so counts and data refresh
  // without reload when a checkpoint is created from overlay (e.g. drill-down without tab visible)
  try {
    const iqOverlayStore = useIQOverlayStore();
    if (iqOverlayStore?.onCheckpointCreated) {
      unsubscribeCheckpointCreated = iqOverlayStore.onCheckpointCreated((payload) => {
        const currentResId = computedResponsibilityId.value;
        if (
          currentResId != null &&
          payload?.responsibilityId != null &&
          String(payload.responsibilityId) === String(currentResId)
        ) {
          const accId = JSON.parse(localStorage.getItem("currentAccountID"));
          if (accId) {
            void refreshResponsibilityDetails(accId, currentResId);
          }
        }
      });
    }
  } catch (e) {
    console.warn("IQ Overlay store not available:", e?.message);
  }
});

onUnmounted(() => {
  if (typeof unsubscribeCheckpointCreated === "function") {
    unsubscribeCheckpointCreated();
  }
});

watch([showAttachedRolesLd, showContextTabLd], () => {
  syncSelectedTabAfterMenuStructureChange();
});

// Watch for route query changes (check both 'tab' and 'resTab')
watch(
  () => proxy?.$route?.query?.resTab || proxy?.$route?.query?.tab,
  (newTab) => {
    applyRouteTabParam(newTab);
  }
);

// Compute badge holder filter levels from route query
const badgeHolderFilterLevels = computed(() => {
  const filter = proxy?.$route?.query?.filter;
  if (filter === "coach") {
    return ["Coach"];
  } else if (filter === "master") {
    return ["Master"];
  }
  return [];
});

const hasPurpose = computed(() => {
  const purpose = responsibilityDetails.value?.purpose;

  if (typeof purpose === "string") {
    return purpose.trim().length > 0;
  }

  return Boolean(purpose);
});

const hasContext = computed(() => {
  const context = responsibilityDetails.value?.context;

  if (typeof context === "string") {
    return context.trim().length > 0;
  }

  return Boolean(context);
});

/** The text the IQ Overlay opens on: what the editor currently shows (unsaved draft, else saved). */
const contextOverlaySeed = computed(
  () => contextDraft.value ?? responsibilityDetails.value?.context ?? ""
);

/** Attached context files count for the Context tab badge (idea-MDP-523). */
const contextFilesCount = computed(() => contextFiles.value?.length ?? 0);

/** ContextTab visual state: "filled" once a context has been saved, otherwise "empty". */
const contextState = computed(() =>
  (responsibilityDetails.value?.context || "").trim() ? "filled" : "empty"
);

// Compute responsibility ID from prop or route
const computedResponsibilityId = computed(() => {
  return props.resId || proxy?.$route?.params?.resId || null;
});

// Compute account ID from localStorage
const accountIdComputed = computed(() => {
  return JSON.parse(localStorage.getItem("currentAccountID"));
});

const responsibilityCheckpointCounts = computed(() => {
  if (!responsibilityDetails.value) {
    return {
      Apprentice: 0,
      Professional: 0,
      Coach: 0,
      Master: 0,
    };
  }

  return {
    Apprentice: responsibilityDetails.value.count_apprentice_checkpoints || 0,
    Professional: responsibilityDetails.value.count_professional_checkpoints || 0,
    Coach: responsibilityDetails.value.count_coach_checkpoints || 0,
    Master: responsibilityDetails.value.count_master_checkpoints || 0,
  };
});

function applyResponsibilityDetailsPayload(data) {
  responsibilityDetails.value = data;
  checklist_count.value =
    data?.active_apprentice_checkpoints +
    data?.active_coach_checkpoints +
    data?.active_master_checkpoints +
    data?.active_professional_checkpoints;
  badge_holders_count.value = data?.badges_count;
  assignments_count.value = data?.assignments_count;
  const activeAttachedRolesCount = resolveActiveAttachedRolesCount(data);
  if (activeAttachedRolesCount !== undefined) {
    attachedRolesMenuCount.value = activeAttachedRolesCount;
  }
  responsibilityDetailsLoaded.value = true;
  if (data?.id && showContextTabV2.value) {
    void loadContextFiles(data.id);
  }
}

/** ER-10294: refresh the Context Files list for this responsibility. */
async function loadContextFiles(resId) {
  if (!showContextTabV2.value) return;
  try {
    const { data } = await getContextFiles("responsibility", resId);
    const list = Array.isArray(data) ? data : data?.results || [];
    contextFiles.value = list.map(mapContextFileToUi);
  } catch (error) {
    console.error("Error loading context files:", error);
  }
}

async function refreshActiveAttachedRolesMenuCount(accountId, resId) {
  if (!showAttachedRolesLd.value || !accountId || !resId || isAttachedRolesTabActive.value) {
    return;
  }

  try {
    const response = await getListOfRolesAttachedToResponsibility(
      accountId,
      1,
      0,
      "",
      resId,
      ACTIVE_ATTACHED_ROLES_FILTERS
    );
    const count = Number(response?.data?.count);
    if (!Number.isNaN(count)) {
      attachedRolesMenuCount.value = count;
    }
  } catch (error) {
    console.warn("Could not refresh active attached roles count:", error?.message || error);
  }
}

/** First load when opening the page. Resets loading state; does not throw (avoids unhandled Axios / Sentry noise). */
async function loadResponsibilityDetails(accountId, resId) {
  responsibilityDetailsLoaded.value = false;
  try {
    proxy.$loader.show();
    const response = await getResponsibilityIdV2(accountId, resId, "responsibility");
    applyResponsibilityDetailsPayload(response.data);
    await refreshActiveAttachedRolesMenuCount(accountId, resId);
    return true;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      proxy?.$store?.dispatch("routeErrorHandling/setError", {
        code: 404,
        message: "Responsibility not found.",
      });
    } else {
      console.warn("Failed to load responsibility details:", error?.message || error);
    }
    return false;
  } finally {
    proxy.$loader.hide();
  }
}

/** Re-fetch after user actions or overlay events. Does not clear `responsibilityDetailsLoaded` first, so the page stays usable if the request fails. */
async function refreshResponsibilityDetails(accountId, resId) {
  try {
    proxy.$loader.show();
    const response = await getResponsibilityIdV2(accountId, resId, "responsibility");
    applyResponsibilityDetailsPayload(response.data);
    await refreshActiveAttachedRolesMenuCount(accountId, resId);
    return true;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      proxy?.$store?.dispatch("routeErrorHandling/setError", {
        code: 404,
        message: "Responsibility not found.",
      });
    } else {
      console.warn("Could not refresh responsibility details:", error?.message || error);
    }
    return false;
  } finally {
    proxy.$loader.hide();
  }
}

function handleTabChange(value) {
  if (selectedTab.value === value) {
    // If clicking the same tab, reset its internal view (e.g. go back to list from checkpoint edit)
    if (value === 0) {
      nextTick(() => {
        editCheckpointsTabRef.value?.resetView?.();
      });
    }
  }
  selectedTab.value = value;

  const tabName = tabIndexToName.value[value];
  if (tabName) {
    pendingResTab.value = tabName;
    replaceResTabQuery(tabName);
  }

  emit("tab-change", value);
}

function handleBreadcrumbNavigation(breadcrumb) {
  const route = proxy?.$route;
  const router = proxy?.$router;

  if (
    props.drilldownInPanel &&
    (breadcrumb?.positionId || breadcrumb?.roleId) &&
    !breadcrumb?.disabled
  ) {
    emit("drilldown", {
      positionId: breadcrumb.positionId ?? route?.params?.positionId ?? route?.query?.positionId,
      roleId: breadcrumb.roleId ?? route?.params?.roleId ?? route?.query?.roleId,
    });
    return;
  }

  if (!router) return;

  const positionId = route?.params?.positionId;
  const roleId = route?.params?.roleId;

  if (breadcrumb?.text === "Badge Holders") {
    showInspectionForm.value = false;
    selectedTab.value = 1;
    badgeHoldersTabRef.value?.closeInspection?.();
    const tabName = tabIndexToName.value[1];
    if (tabName) {
      pendingResTab.value = tabName;
      replaceResTabQuery(tabName);
    }
    return;
  }

  // Navigate to Positions list
  if (breadcrumb?.text === "Positions") {
    router
      .push({
        name: "NewOrganization",
        params: { tab: "positions" },
      })
      .catch(() => {});
  }
  // Navigate to Roles list
  else if (breadcrumb?.text === "Roles") {
    router
      .push({
        name: "NewOrganization",
        params: { tab: "roles" },
      })
      .catch(() => {});
  }
  // Return to the As A Master table in place (embedded in the same route)
  else if (breadcrumb?.text === "As an Architect") {
    const query = { ...route.query };
    delete query.from;
    delete query.resId;
    delete query.resTab;
    delete query.tab;
    delete query.filter;
    router.replace({ path: route.path, query }).catch(() => {});
    emit("close-embedded");
  }
  // Navigate to Responsibilities list (or back to embedded list when we came from there)
  else if (breadcrumb?.text === "Responsibilities") {
    if (route?.query?.from === "your-stuff-directories") {
      const query = { ...route.query };
      delete query.from;
      delete query.resTab;
      delete query.tab;
      delete query.filter;
      router.replace({ path: route.path, query }).catch(() => {});
      emit("close-embedded");
    } else {
      router
        .push({
          name: "NewOrganization",
          params: { tab: "responsibilities" },
        })
        .catch(() => {});
    }
  }
  // Navigate back to Position detail
  else if (breadcrumb?.positionId && !breadcrumb.disabled) {
    router
      .push({
        name: "PositionDetailChild",
        params: { positionId: breadcrumb.positionId },
        query: { positionTab: "attached-roles" },
      })
      .catch(() => {});
  }
  // Navigate back to Role detail
  else if (breadcrumb?.roleId && !breadcrumb.disabled) {
    // If we have both positionId and roleId, navigate to role from position
    if (positionId) {
      router
        .push({
          name: "RoleDetailFromPosition",
          params: {
            positionId: positionId,
            roleId: breadcrumb.roleId,
          },
          query: {
            positionName: route?.query?.positionName,
            roleTab: "responsibilities",
          },
        })
        .catch(() => {});
    } else {
      // Otherwise, navigate to standalone role
      router
        .push({
          name: "RoleDetailChild",
          params: { roleId: breadcrumb.roleId },
          query: { roleTab: "responsibilities" },
        })
        .catch(() => {});
    }
  }
}

async function handleCheckpointCountChanged() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;
  if (!accountId || !resId) return;
  await refreshResponsibilityDetails(accountId, resId);
}

function handleDrilldown(data) {
  emit("drilldown", data);
}

function handlePurposeInput(value) {
  emit("purpose-input", value);
}

async function handlePurposeSave(value) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    isSavingPurpose.value = true;
    await updateResponsibilityPurpose(accountId, resId, {
      purpose: value,
      account: accountId,
    });
    await refreshResponsibilityDetails(accountId, resId);
  } catch (error) {
    console.error("Error saving purpose:", error);
  } finally {
    isSavingPurpose.value = false;
  }
}

async function handleContextSave(value) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    isSavingContext.value = true;
    await updateResponsibilityContext(accountId, resId, {
      context: value,
    });
    await refreshResponsibilityDetails(accountId, resId);
    clearContextDraft();
  } catch (error) {
    console.error("Error saving context:", error);
  } finally {
    isSavingContext.value = false;
  }
}

function handleContextSuggest() {
  if (contextIQOverlayEnabled.value) {
    isContextIQOverlayOpen.value = true;
    return undefined;
  }
  return suggestContextText(computedResponsibilityId.value);
}

function handleContextImprove() {
  if (contextIQOverlayEnabled.value) {
    isContextIQOverlayOpen.value = true;
  }
}

/** Keep the overlay's text as the editor draft until the save lands, so a failed save loses nothing. */
function handleContextOverlaySave(value) {
  setContextDraft(value);
  return handleContextSave(value);
}

async function handleContextUpload(fileList) {
  const resId = computedResponsibilityId.value;
  if (!fileList?.length || !resId) return;

  uploadingContextFiles.value = Array.from(fileList).map((file) => ({
    name: file.name,
    progress: 0,
  }));

  try {
    isSavingContext.value = true;
    await uploadContextFiles("responsibility", resId, fileList, (event) => {
      if (!event.total) return;
      const progress = Math.round((event.loaded / event.total) * 100);
      uploadingContextFiles.value = uploadingContextFiles.value.map((file) => ({
        ...file,
        progress,
      }));
    });
    await loadContextFiles(resId);
  } catch (error) {
    console.error("Error uploading context file:", error);
  } finally {
    isSavingContext.value = false;
    uploadingContextFiles.value = [];
  }
}

async function handleContextRemove(fileId) {
  const resId = computedResponsibilityId.value;
  if (!fileId || !resId) return;

  try {
    isSavingContext.value = true;
    await deleteContextFile(fileId);
    await loadContextFiles(resId);
  } catch (error) {
    console.error("Error removing context file:", error);
  } finally {
    isSavingContext.value = false;
  }
}

function handlePurposeDiscard() {
  emit("purpose-discard");
}

function handleAddPurpose() {
  emit("add-purpose");
}

function handlePurposeEdit() {
  emit("purpose-edit");
}

function handlePurposeImprove(value) {
  emit("purpose-improve", value);
}

function handlePurposeSuggest() {
  emit("purpose-suggest");
}

// Assignments Tab Handlers
function handleWeightChanged(data) {
  emit("weight-changed", data);
}

// Defaults Tab Handlers
function handleCreateDefaultBadge(data) {
  emit("create-default-badge", data);
}

async function handleBadgeCountsUpdated() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;
  if (!accountId || !resId) return;
  await refreshResponsibilityDetails(accountId, resId);
}

async function handleAttachedRolesDataUpdated() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;
  if (accountId && resId) {
    await refreshResponsibilityDetails(accountId, resId);
  }
  emit("responsibilityDataUpdated");
}

function handleAttachedRolesCountUpdated(payload) {
  if (payload && typeof payload.count === "number") {
    attachedRolesMenuCount.value = payload.count;
  }
}

// --- Inspection flow (same behaviour as EmployeeDetail) ---
function getInspectionLevelName(level) {
  const levelMap = { 1: "Apprentice", 2: "Professional", 3: "Coach", 4: "Master" };
  return levelMap[level] || "Apprentice";
}
function getInspectionStatusView(status) {
  if (!status) return "pending-evaluation";
  const m = {
    passed: "passed-evaluation",
    failed: "failed-evaluation",
    not_applicable: "notapplicable-evaluation",
    reset: "reset-pending-evaluation",
    pending: "pending-evaluation",
  };
  return m[status] || "pending-evaluation";
}
function inspectionExtractCode(codeStr) {
  if (codeStr == null && codeStr !== 0) return "";
  if (typeof codeStr === "number") return `#${codeStr}`;
  const s = String(codeStr);
  const match = s.match(/#?(\d+)/);
  return match ? `#${match[1]}` : s.startsWith("#") ? s : `#${s}`;
}
function getInspectionBadgeLevelNumber(level) {
  if (!level || typeof level !== "string") return 1;
  const m = { apprentice: 1, proficient: 2, coach: 3, architect: 4 };
  return m[normalizeBadgeLevel(level)] || 1;
}

async function handleInspectionOpen(payload) {
  const employee = payload?.employee;
  if (!employee?.id) return;
  await loadInspectionDataForEmployee(employee);
  if (inspectionFormattedResponsibility.value) {
    showInspectionForm.value = true;
  }
}

async function loadInspectionDataForEmployee(employee) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;
  const respData = responsibilityDetails.value;
  if (!accountId || !resId || !respData || !employee?.id) {
    inspectionFormattedResponsibility.value = null;
    return;
  }
  inspectionEmployeeId.value = employee.id;
  inspectionEmployeeAccess.value = Object.prototype.hasOwnProperty.call(employee, "access")
    ? employee.access
    : null;
  const respCode = respData.code_str
    ? inspectionExtractCode(respData.code_str)
    : `#${respData.id || ""}`;
  inspectionFormattedResponsibility.value = {
    responsibilityCode: respCode,
    responsibilityName: respData.name || "",
    responsibilityId: resId,
    responsibilityImportance: 3,
    roleCode: inspectionContextRoleCode.value || "",
    roleName: inspectionContextRoleName.value || "",
    roleId: "",
    roleImportance: 3,
    positionCode: inspectionContextPositionCode.value || "",
    positionName: inspectionContextPositionName.value || employee.title || employee.position || "",
    positionId: "",
    positionImportance: 3,
    positionCommitment: "Primary",
    assignedOn: null,
    newDuration: 15,
    show_codes: true,
  };
  inspectionFormattedEmployee.value = {
    firstName: employee.firstName ?? employee.first_name ?? "",
    lastName: employee.lastName ?? employee.last_name ?? "",
    position: employee.title ?? employee.position ?? "",
    photoUrl: employee.avatar ?? employee.photoUrl ?? "",
  };
  let currentBadgeData = null;
  try {
    const badgesRes = await getEmployeeBadges(accountId, employee.id);
    const badges = badgesRes?.data?.results || [];
    const extractHashCode = (str) => {
      if (!str && str !== 0) return "";
      const m = String(str).match(/#\d+/);
      return m ? m[0] : String(str);
    };
    const code =
      extractHashCode(respData?.code_str) ||
      (respData?.code != null && respData?.code !== ""
        ? `#${String(respData.code).replace(/^#/, "")}`
        : "");
    const matchCtx = code ? { responsibilityCode: code } : {};
    currentBadgeData = pickBadgeForResponsibility(badges, resId, matchCtx);
  } catch (_) {}
  inspectionCurrentBadgeData.value = currentBadgeData;
  if (currentBadgeData) {
    const lvl = currentBadgeData.level || "novice";
    inspectionCurrentBadgeLevel.value =
      typeof lvl === "string"
        ? lvl.charAt(0).toUpperCase() + lvl.slice(1)
        : getInspectionLevelName(lvl);
    const statusMap = {
      active: "Good Standing",
      good_standing: "Good Standing",
      appointed: "Appointed",
      given: "Given",
      warning: "No Dot",
      issue: "No Dot",
      obsolete: "Obsolete",
      no_dot: "No Dot",
    };
    inspectionDot.value = resolveAppointedDot(currentBadgeData, statusMap[currentBadgeData.status] || "Good Standing");
  } else {
    inspectionCurrentBadgeLevel.value = "Novice";
    inspectionDot.value = "Good Standing";
  }
  try {
    proxy.$loader?.show();
    const respDetailRes = await getResponsibilityIdV2(accountId, resId, "responsibility");
    const respDetail = respDetailRes?.data;
    if (respDetail) {
      inspectionFormattedResponsibility.value = {
        ...inspectionFormattedResponsibility.value,
        responsibilityCode:
          respDetail.code_str || inspectionFormattedResponsibility.value.responsibilityCode,
        responsibilityName:
          respDetail.name || inspectionFormattedResponsibility.value.responsibilityName,
      };
      inspectionLevelCounter.value = {
        Apprentice:
          respDetail.active_apprentice_checkpoints || respDetail.count_apprentice_checkpoints || 0,
        Professional:
          respDetail.active_professional_checkpoints ||
          respDetail.count_professional_checkpoints ||
          0,
        Coach: respDetail.active_coach_checkpoints || respDetail.count_coach_checkpoints || 0,
        Master: respDetail.active_master_checkpoints || respDetail.count_master_checkpoints || 0,
      };
      const total =
        (respDetail.active_apprentice_checkpoints || 0) +
        (respDetail.active_professional_checkpoints || 0) +
        (respDetail.active_coach_checkpoints || 0) +
        (respDetail.active_master_checkpoints || 0);
      if (total > 0) {
        // Call evaluate API to get position/role when there are active checkpoints
        try {
          const evaluateRes = await getAssignmentEval(accountId, resId, "responsibility");
          const evalData = evaluateRes?.data;
          const hasEvalData =
            evalData &&
            !Array.isArray(evalData) &&
            evalData.responsibility &&
            evalData.role &&
            evalData.position;
          if (hasEvalData) {
            const extractCodeFromObj = (obj) => {
              const str = obj?.code_str;
              if (!str) return obj?.code ? `#${obj.code}` : "";
              const m = String(str).match(/#?(\d+)/);
              return m ? `#${m[1]}` : String(str).startsWith("#") ? str : `#${str}`;
            };
            inspectionFormattedResponsibility.value = {
              ...inspectionFormattedResponsibility.value,
              responsibilityCode:
                extractCodeFromObj(evalData.responsibility) ||
                inspectionFormattedResponsibility.value.responsibilityCode,
              responsibilityName:
                evalData.responsibility?.name ||
                inspectionFormattedResponsibility.value.responsibilityName,
              roleCode: extractCodeFromObj(evalData.role) || "",
              roleName: evalData.role?.name || "",
              positionCode: extractCodeFromObj(evalData.position) || "",
              positionName: evalData.position?.name || "",
            };
          } else {
            // Empty array or missing data: show just responsibility name (no role/position)
            inspectionFormattedResponsibility.value = {
              ...inspectionFormattedResponsibility.value,
              roleCode: "",
              roleName: "",
              positionCode: "",
              positionName: "",
            };
          }
        } catch (_) {
          // Ignore; fall back to responsibility-only display
        }
        const limit = total;
        const evalRes = await getListActiveCheckpointsEvalV2(
          accountId,
          resId,
          employee.id,
          limit,
          0
        );
        if (evalRes?.data?.results) {
          inspectionData.value = evalRes.data.results.map((item) => ({
            id: item.checkpoint,
            checkpoint_id: item.checkpoint,
            level: getInspectionLevelName(item.proficiency_level),
            level_name: getInspectionLevelName(item.proficiency_level),
            proficiency_level: item.proficiency_level,
            criteria: item.checkpoint_criteria,
            instructions: item.checkpoint_instructions,
            video:
              item.microlearning &&
              typeof item.microlearning === "string" &&
              (item.microlearning.includes("<iframe") || item.microlearning.includes("http"))
                ? item.microlearning
                : "",
            status: item.status,
            evaluation_status: item.status,
            inspectionStatus: getInspectionStatusView(item.status),
            isPassed: item.status === "passed",
            isFailed: item.status === "failed",
            isNotApplicable: item.status === "not_applicable",
            failed_by: item.evaluated_by?.name || null,
            failed_on: item.evaluated_on,
            failed_reason: item.reason,
            first_name:
              item.evaluated_by?.first_name || item.evaluated_by?.name?.split(" ")[0] || "",
            last_name:
              item.evaluated_by?.last_name ||
              item.evaluated_by?.name?.split(" ").slice(1).join(" ") ||
              "",
            updated_on: item.evaluated_on,
            updated_by: item.evaluated_by || null,
            avatar: item.evaluator_avatar || item.evaluated_by?.picture || "",
            not_applicable_reason: item.status === "not_applicable" ? item.reason : "",
          }));
        }
        showNoInspectionRecords.value = (inspectionData.value?.length || 0) === 0;
        showInspectionPagination.value = (evalRes?.data?.count || 0) > 10;
        inspectionPagination.value = { total: evalRes?.data?.count || 0, page: 1, perPage: 10 };
      } else {
        inspectionData.value = [];
        showNoInspectionRecords.value = true;
      }
    } else {
      inspectionData.value = [];
      showNoInspectionRecords.value = true;
    }
  } catch (err) {
    console.warn("Could not load inspection data:", err);
    try {
      const checklistRes = await getResponsibilityChecklistV2(accountId, resId, {
        status: "active",
      });
      if (checklistRes?.data && Array.isArray(checklistRes.data)) {
        inspectionData.value = checklistRes.data.map((item) => ({
          id: item.id,
          checkpoint_id: item.id,
          level: getInspectionLevelName(item.proficiency_level),
          level_name: getInspectionLevelName(item.proficiency_level),
          proficiency_level: item.proficiency_level,
          criteria: item.criteria,
          instructions: item.instructions,
          video:
            item.microlearning &&
            typeof item.microlearning === "string" &&
            (item.microlearning.includes("<iframe") || item.microlearning.includes("http"))
              ? item.microlearning
              : "",
          status: item.status,
          evaluation_status: "pending",
          inspectionStatus: "pending-evaluation",
          isPassed: false,
          isFailed: false,
          isNotApplicable: false,
          failed_by: null,
          failed_on: null,
          failed_reason: null,
          first_name: "",
          last_name: "",
          updated_on: null,
          updated_by: null,
          avatar: "",
          not_applicable_reason: "",
        }));
      }
      showNoInspectionRecords.value = (inspectionData.value?.length || 0) === 0;
    } catch (_) {
      inspectionData.value = [];
      showNoInspectionRecords.value = true;
    }
  } finally {
    proxy.$loader?.hide();
  }
  inspectionFilterText.value = "";
  showInspectionPagination.value = false;
}

async function syncInspectionBadgeStateFromApi() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const empId = inspectionEmployeeId.value;
  const respData = responsibilityDetails.value;
  if (!accountId || !empId || !respData) return;
  try {
    const badgesRes = await getEmployeeBadges(accountId, empId);
    const badges = badgesRes?.data?.results || [];
    const currentBadgeData = badges.find(
      (b) =>
        b.is_current &&
        (b.issued_for?.id === respData.id ||
          b.issued_for?.code === respData.code_str ||
          b.responsibility?.id === respData.id)
    );
    inspectionCurrentBadgeData.value = currentBadgeData;
    if (currentBadgeData) {
      const lvl = currentBadgeData.level || "novice";
      inspectionCurrentBadgeLevel.value =
        typeof lvl === "string"
          ? lvl.charAt(0).toUpperCase() + lvl.slice(1).toLowerCase()
          : getInspectionLevelName(lvl);
      const statusMap = {
        active: "Good Standing",
        good_standing: "Good Standing",
        appointed: "Appointed",
        given: "Given",
        warning: "No Dot",
        issue: "No Dot",
        obsolete: "Obsolete",
        no_dot: "No Dot",
      };
      inspectionDot.value = resolveAppointedDot(currentBadgeData, statusMap[currentBadgeData.status] || "Good Standing");
    } else {
      inspectionCurrentBadgeLevel.value = "Novice";
      inspectionDot.value = "Good Standing";
    }
  } catch (e) {
    console.warn("syncInspectionBadgeStateFromApi:", e);
  }
}

async function reloadInspectionData() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || !inspectionEmployeeId.value) return;
  try {
    const evalRes = await getListActiveCheckpointsEvalV2(
      accountId,
      computedResponsibilityId.value,
      inspectionEmployeeId.value,
      100,
      0
    );
    if (evalRes?.data?.results) {
      inspectionData.value = evalRes.data.results.map((item) => ({
        id: item.checkpoint,
        checkpoint_id: item.checkpoint,
        level: getInspectionLevelName(item.proficiency_level),
        level_name: getInspectionLevelName(item.proficiency_level),
        proficiency_level: item.proficiency_level,
        criteria: item.checkpoint_criteria,
        instructions: item.checkpoint_instructions,
        video:
          item.microlearning &&
          typeof item.microlearning === "string" &&
          (item.microlearning.includes("<iframe") || item.microlearning.includes("http"))
            ? item.microlearning
            : "",
        status: item.status,
        evaluation_status: item.status,
        inspectionStatus: getInspectionStatusView(item.status),
        isPassed: item.status === "passed",
        isFailed: item.status === "failed",
        isNotApplicable: item.status === "not_applicable",
        failed_by: item.evaluated_by?.name || null,
        failed_on: item.evaluated_on,
        failed_reason: item.reason,
        first_name: item.evaluated_by?.first_name || item.evaluated_by?.name?.split(" ")[0] || "",
        last_name:
          item.evaluated_by?.last_name ||
          item.evaluated_by?.name?.split(" ").slice(1).join(" ") ||
          "",
        updated_on: item.evaluated_on,
        updated_by: item.evaluated_by || null,
        avatar: item.evaluator_avatar || item.evaluated_by?.picture || "",
        not_applicable_reason: item.status === "not_applicable" ? item.reason : "",
      }));
    }
  } catch (_) {}
}

function handleInspectionBreadcrumbClick(breadcrumb) {
  if (breadcrumb?.text === "Badge Holders") {
    handleCloseInspection();
  } else {
    handleBreadcrumbNavigation(breadcrumb);
  }
}

async function handleInspectionBadgeSelected(payload) {
  if (!inspectionEmployeeId.value || !inspectionFormattedResponsibility.value) return;
  if (payload?.can_remove || payload?.isRemoveAction) {
    if (inspectionCurrentBadgeData.value) {
      handleInspectionRemoveBadgeClick(inspectionCurrentBadgeData.value);
    }
    return;
  }
  const levelString =
    payload?.value ??
    payload?.Label ??
    payload?.level ??
    (typeof payload === "string" ? payload : null);
  if (!levelString) {
    proxy.$notifications?.error("Invalid badge selection");
    return;
  }
  const levelLower = String(levelString).toLowerCase();
  const grandLabel = String(payload?.grand_label || "").toLowerCase();

  // Path to Readiness / GrandBadgeDropdown: "Award …" → confirmation dialog; "Give …" → GiveBadgeDialog (locked tier)
  if (grandLabel.startsWith("award")) {
    inspectionAwardBadgeLevel.value = levelLower;
    showInspectionAwardBadgeDialog.value = true;
    return;
  }

  inspectionGiveBadgeLockedLevel.value = levelLower;
  inspectionGiveBadgeDefaultLevel.value = levelLower;
  showInspectionGiveBadgeDialog.value = true;
}

function onInspectionGiveBadgeDialogInput(visible) {
  showInspectionGiveBadgeDialog.value = visible;
  if (!visible) {
    inspectionGiveBadgeLockedLevel.value = null;
    inspectionGiveBadgeDefaultLevel.value = "apprentice";
  }
}

function closeInspectionGiveBadgeDialog() {
  showInspectionGiveBadgeDialog.value = false;
  inspectionGiveBadgeLockedLevel.value = null;
  inspectionGiveBadgeDefaultLevel.value = "apprentice";
}

async function handleInspectionGiveBadgeConfirm({ level, notifyEmployee }) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || !inspectionEmployeeId.value) return;
  try {
    proxy.$loader?.show();
    showInspectionGiveBadgeDialog.value = false;
    inspectionGiveBadgeLockedLevel.value = null;
    inspectionGiveBadgeDefaultLevel.value = "apprentice";
    const levelNum = getInspectionBadgeLevelNumber(String(level || "").toLowerCase());
    await createProficiencyBadge(accountId, {
      employee: inspectionEmployeeId.value,
      responsibility: computedResponsibilityId.value,
      level: levelNum,
      given: true,
      issued_method: "given",
      notify: !!notifyEmployee,
    });
    await syncInspectionBadgeStateFromApi();
    await reloadInspectionData();
    await handleBadgeCountsUpdated();
    proxy.$notifications?.success("Badge issued successfully");
  } catch (error) {
    console.error("Error giving badge:", error);
    proxy.$notifications?.error("Error issuing badge");
  } finally {
    proxy.$loader?.hide();
  }
}

function getInspectionBadgeLevelName(level) {
  if (typeof level === "string") return BADGE_LEVEL_DISPLAY_LABELS[level.toLowerCase()] || level;
  return getBadgeLevelLabel(level >= 1 && level <= 4 ? level : 1);
}
function handleInspectionRemoveBadgeClick(badgeData) {
  if (!badgeData || !badgeData.id) {
    proxy.$notifications?.error("Invalid badge data");
    return;
  }
  const badgeLevel = badgeData.level || "";
  selectedBadgeForInspectionRemoval.value = {
    id: badgeData.id,
    badgeName: getInspectionBadgeLevelName(badgeLevel),
    level: badgeLevel,
    responsibilityName: inspectionFormattedResponsibility.value?.responsibilityName || "",
    employeeName:
      [inspectionFormattedEmployee.value?.firstName, inspectionFormattedEmployee.value?.lastName]
        .filter(Boolean)
        .join(" ") || "Employee",
  };
  showInspectionRemoveBadgeDialog.value = true;
}
async function handleConfirmInspectionRemoveBadge({ reason: removeReason }) {
  if (!selectedBadgeForInspectionRemoval.value?.id || !removeReason?.trim()) {
    proxy.$notifications?.error("Please provide a reason for removing the badge");
    return;
  }
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  try {
    proxy.$loader?.show();
    await deleteResponsibilityBadge(accountId, selectedBadgeForInspectionRemoval.value.id, {
      badge: selectedBadgeForInspectionRemoval.value.id,
      method: "removed",
      reason: removeReason.trim(),
    });
    proxy.$notifications?.success("Badge removed successfully");
    showInspectionRemoveBadgeDialog.value = false;
    selectedBadgeForInspectionRemoval.value = null;
    await syncInspectionBadgeStateFromApi();
    await reloadInspectionData();
    await handleBadgeCountsUpdated();
  } catch (error) {
    console.error("Error removing badge:", error);
    proxy.$notifications?.error(error?.response?.data?.message || "Error removing badge");
  } finally {
    proxy.$loader?.hide();
  }
}
function handleCloseInspectionRemoveBadgeDialog() {
  showInspectionRemoveBadgeDialog.value = false;
  selectedBadgeForInspectionRemoval.value = null;
}
function handleInspectionSearch(searchTerm) {
  inspectionFilterText.value = searchTerm;
}
function handleInspectionClick() {}
function handleInspectionRemoveFilter(filter) {
  inspectionChipText.value = inspectionChipText.value.filter((c) => c !== filter);
}
function handleInspectionFilterTypes() {}
function handleInspectionPagination(pagination) {
  inspectionPagination.value = pagination;
}
function handleInspectionClearSelection() {
  inspectionChipText.value = [];
}
function handleInspectionCheckedItemsChanged(items) {
  inspectionChipText.value = items
    .map((item) => (typeof item === "string" ? item : item.value))
    .filter(Boolean);
}

function getCardInspectionBadgeLevelNumber() {
  const b = inspectionCurrentBadgeData.value;
  if (!b || b.level == null) return 0;
  if (typeof b.level === "number") {
    return b.level >= 1 && b.level <= 4 ? b.level : 0;
  }
  const m = { novice: 0, apprentice: 1, proficient: 2, coach: 3, architect: 4 };
  return m[normalizeBadgeLevel(String(b.level))] ?? 0;
}

function getCardRowLevelNumber(level) {
  if (!level) return 0;
  const m = { novice: 0, apprentice: 1, proficient: 2, coach: 3, architect: 4 };
  const s = normalizeBadgeLevel(String(level));
  if (m[s] !== undefined) return m[s];
  const title = ["novice", "apprentice", "proficient", "coach", "architect"];
  const idx = title.indexOf(s);
  return idx >= 0 ? idx : (m[s] ?? 0);
}

function isCardLevelCompleteForAward(level) {
  return isLevelCompleteForAward(level, inspectionData.value || []);
}

function shouldShowCardAwardBadgeForRowLevel(level) {
  if (!isCardLevelCompleteForAward(level)) return false;
  const currentNum = getCardInspectionBadgeLevelNumber();
  const awardNum = getCardRowLevelNumber(level);
  return awardNum > 0 && awardNum > currentNum;
}

async function handleInspectionPass(row) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  try {
    proxy.$loader?.show();
    const chkpId = row.id || row.checkpoint_id || row.checkpoint;
    await passCheckPointEvaluation(accountId, computedResponsibilityId.value, chkpId, {
      employee: inspectionEmployeeId.value,
      checkpoint: chkpId,
      reason: "passed",
      status: "passed",
    });
    await reloadInspectionData();
    await nextTick();
    let levelToShow = null;
    if (row?.level && shouldShowCardAwardBadgeForRowLevel(row.level)) {
      levelToShow = row.level;
    }
    if (levelToShow) {
      inspectionAwardBadgeLevel.value = String(levelToShow).toLowerCase();
      showInspectionAwardBadgeDialog.value = true;
    } else {
      proxy.$notifications?.success("Checkpoint passed successfully");
    }
  } catch (error) {
    const msg = proficiencyCheckpointErrorMessage(error) || "Error passing checkpoint";
    proxy.$notifications?.error(msg);
    console.error("Error passing checkpoint:", error);
  } finally {
    proxy.$loader?.hide();
  }
}

function handleInspectionFail(row) {
  inspectionSelectedCheckpoint.value = row;
  inspectionReason.value =
    row.failed_reason && row.evaluation_status === "failed" ? row.failed_reason : "";
  inspectionReasonModal.value.type = "fail";
  inspectionReasonModal.value.config = inspectionReasonModalConfigs.fail;
  inspectionReasonModal.value.show = true;
}
function handleInspectionNotApplicable(row) {
  inspectionSelectedCheckpoint.value = row;
  inspectionReason.value = "";
  inspectionReasonModal.value.type = "notApplicable";
  inspectionReasonModal.value.config = inspectionReasonModalConfigs.notApplicable;
  inspectionReasonModal.value.show = true;
}
function closeInspectionReasonModal() {
  inspectionReasonModal.value.show = false;
  inspectionReasonModal.value.type = null;
  inspectionReasonModal.value.config = {};
  inspectionReason.value = "";
  inspectionSelectedCheckpoint.value = null;
}
async function handleInspectionReasonAction() {
  if (inspectionReasonModal.value.type === "fail")
    await executeInspectionFail(inspectionSelectedCheckpoint.value);
  else if (inspectionReasonModal.value.type === "notApplicable")
    await executeInspectionNotApplicable(inspectionSelectedCheckpoint.value);
}
async function executeInspectionFail(checkpointData) {
  if (!inspectionReason.value?.trim()) {
    proxy.$notifications?.error("Please provide a reason");
    return;
  }
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  try {
    proxy.$loader?.show();
    const chkpId = checkpointData.id || checkpointData.checkpoint_id || checkpointData.checkpoint;
    await failCheckPointEvaluation(accountId, computedResponsibilityId.value, chkpId, {
      employee: inspectionEmployeeId.value,
      checkpoint: chkpId,
      reason: inspectionReason.value,
      status: "failed",
    });
    closeInspectionReasonModal();
    await reloadInspectionData();
    proxy.$notifications?.success("Checkpoint marked as failed");
  } catch (error) {
    closeInspectionReasonModal();
    const msg = proficiencyCheckpointErrorMessage(error) || "Error failing checkpoint";
    proxy.$notifications?.error(msg);
    console.error("Error failing checkpoint:", error);
  } finally {
    proxy.$loader?.hide();
  }
}
async function executeInspectionNotApplicable(checkpointData) {
  if (!inspectionReason.value?.trim()) {
    proxy.$notifications?.error("Please provide a reason");
    return;
  }
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  try {
    proxy.$loader?.show();
    const chkpId = checkpointData.id || checkpointData.checkpoint_id || checkpointData.checkpoint;
    await notApplicableCheckPointEvaluation(accountId, computedResponsibilityId.value, chkpId, {
      employee: inspectionEmployeeId.value,
      checkpoint: chkpId,
      reason: inspectionReason.value,
      status: "not_applicable",
    });
    closeInspectionReasonModal();
    await reloadInspectionData();
    proxy.$notifications?.success("Checkpoint marked as not applicable");
  } catch (error) {
    closeInspectionReasonModal();
    const msg =
      proficiencyCheckpointErrorMessage(error) || "Error marking checkpoint as not applicable";
    proxy.$notifications?.error(msg);
    console.error("Error marking checkpoint as not applicable:", error);
  } finally {
    proxy.$loader?.hide();
  }
}

function handleInspectionResetOrClose(row) {
  if (row && (row.id || row.checkpoint_id || row.checkpoint)) {
    inspectionCheckpointToReset.value = row;
    showInspectionResetConfirm.value = true;
    return;
  }
  handleCloseInspection();
}
async function handleConfirmResetInspectionEvaluation() {
  if (!inspectionCheckpointToReset.value) {
    showInspectionResetConfirm.value = false;
    inspectionCheckpointToReset.value = null;
    return;
  }
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  try {
    proxy.$loader?.show();
    const chkpId =
      inspectionCheckpointToReset.value.id ||
      inspectionCheckpointToReset.value.checkpoint_id ||
      inspectionCheckpointToReset.value.checkpoint;
    await resetCheckPointEvaluation(accountId, computedResponsibilityId.value, chkpId, {
      employee: inspectionEmployeeId.value,
      checkpoint: chkpId,
      reason: "Reset by coach",
      status: "reset",
    });
    showInspectionResetConfirm.value = false;
    inspectionCheckpointToReset.value = null;
    await reloadInspectionData();
    proxy.$notifications?.success("Evaluation reset successfully");
  } catch (error) {
    console.error("Error resetting checkpoint:", error);
    proxy.$notifications?.error("Error resetting evaluation");
  } finally {
    proxy.$loader?.hide();
    showInspectionResetConfirm.value = false;
    inspectionCheckpointToReset.value = null;
  }
}

function closeInspectionAwardBadgeDialog() {
  showInspectionAwardBadgeDialog.value = false;
  inspectionAwardBadgeLevel.value = "";
}
function skipInspectionAwardBadge() {
  closeInspectionAwardBadgeDialog();
}
async function activateInspectionAwardBadge(isNotify) {
  try {
    proxy.$loader?.show();
    const level = getInspectionBadgeLevelNumber(inspectionAwardBadgeLevel.value);
    await createProficiencyBadge(JSON.parse(localStorage.getItem("currentAccountID")), {
      employee: inspectionEmployeeId.value,
      responsibility: computedResponsibilityId.value,
      level,
      given: false,
      issued_method: "awarded",
      notify: isNotify || false,
    });
    showInspectionAwardBadgeDialog.value = false;
    inspectionAwardBadgeLevel.value = "";
    await syncInspectionBadgeStateFromApi();
    await reloadInspectionData();
    await handleBadgeCountsUpdated();
    proxy.$notifications?.success("Badge awarded successfully");
  } catch (error) {
    console.error("Error awarding badge:", error);
    proxy.$notifications?.error("Error awarding badge");
  } finally {
    proxy.$loader?.hide();
  }
}

function handleCloseInspection() {
  showInspectionForm.value = false;
  inspectionData.value = [];
  inspectionFilterText.value = "";
  inspectionFormattedResponsibility.value = null;
  inspectionFormattedEmployee.value = null;
  inspectionCurrentBadgeLevel.value = null;
  inspectionDot.value = "Good Standing";
  inspectionEmployeeId.value = null;
  inspectionEmployeeAccess.value = null;
  inspectionCurrentBadgeData.value = null;
  showInspectionAwardBadgeDialog.value = false;
  inspectionAwardBadgeLevel.value = "";
  showInspectionGiveBadgeDialog.value = false;
  inspectionGiveBadgeLockedLevel.value = null;
  inspectionGiveBadgeDefaultLevel.value = "apprentice";
  selectedTab.value = 1;
  const tabName = tabIndexToName.value[1];
  if (tabName) {
    pendingResTab.value = tabName;
    replaceResTabQuery(tabName);
  }
  badgeHoldersTabRef.value?.closeInspection?.();
}

function getOrgCodeName(data) {
  if (data?.code_str) {
    return data?.code_str.split(" ")[0];
  } else {
    return "#" + data?.code;
  }
}

// Chatter functions
async function fetchChatterList(isLoadMore = false) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  if (!accountId || !resId) return;

  if (!isLoadMore) {
    chatterList.value = [];
    loadedChatterPages.value.clear();
  }

  const currentLength = chatterList.value.length;
  const page = Math.floor(currentLength / 10) + 1;

  // Don't reload already loaded pages
  if (loadedChatterPages.value.has(page)) {
    return;
  }

  try {
    const { data } = await getResponsibilityChatterV2(accountId, resId, page, "responsibility");
    loadedChatterPages.value.add(page);

    if (data && data.count) {
      totalChatterCount.value = data.count;
    }

    if (data && data.results) {
      const chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        const nameParts = el.recorded_by_name.split(" ");

        const newEl = {
          id: el.id,
          IsExIQtiveBot: false,
          message: el.note,
          created_on: millisecondsString,
          IsHistory: el.is_history,
        };

        if (nameParts.length === 2) {
          newEl.avatar = {
            picture: "",
            firstName: nameParts[0],
            lastName: nameParts[1],
          };
        } else {
          newEl.avatar = {
            picture: "",
            firstName: el.recorded_by_name,
            lastName: "",
          };
        }

        return newEl;
      });
      chatterList.value.push(...chatterItems);
    }
  } catch (error) {
    console.error("Error fetching chatter:", error);
  }
}

function handleReloadChatterList() {
  fetchChatterList(false);
}

function handleRequestMoreChatter() {
  fetchChatterList(true);
}

// Dropdown Menu Handler
async function handleOptionClick(operation) {
  const action = operation.item?.value;

  switch (action) {
    case "activate_action":
      await openActivationConfirmation("activate");
      break;
    case "rename_action":
      showRenameDialog.value = true;
      break;
    case "delete_action":
      showDeleteDialog.value = true;
      break;
    case "retire_action":
      showRetireDialog.value = true;
      break;
    case "attach_role":
      // Reset pagination state
      currentPage.value = 1;
      currentSearchTerm.value = "";
      hasMoreData.value = true;
      loadAttachableRoles(1, "", false);
      showAttachRoleDialog.value = true;
      break;
    case "reactivate_action":
      await openActivationConfirmation("reactivate");
      break;
    case "archive_action":
      archiveAction.value = "archive";
      showArchiveDialog.value = true;
      break;
    case "unarchive_action":
      archiveAction.value = "unarchive";
      unarchiveResponsibility.start({
        id: computedResponsibilityId.value,
        name: responsibilityDetails.value?.name,
      });
      break;
    default:
  }
}

// Dialog Action Handlers
async function handleResponsibilityRenamed() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    proxy.$loader.setAppLoading(true);
    await refreshResponsibilityDetails(accountId, resId);
    emit("responsibilityDataUpdated");
  } catch (error) {
    console.error("Error refreshing responsibility after rename:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
}

async function handleDeleteResponsibility() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    proxy.$loader.setAppLoading(true);
    await deleteResponsibility(accountId, resId);
    showDeleteDialog.value = false;
    if (props.drilldownInPanel) {
      emit("item-deleted", { type: "responsibility", id: resId });
    } else {
      proxy.$notifications?.success?.("Responsibility successfully deleted");
      proxy.$router.push("/organization/responsibilities").catch(() => {});
    }
  } catch (error) {
    console.error("Error deleting responsibility:", error);
    proxy.$notifications.error(getDeleteErrorMessage(error, { entity: "responsibility" }));
  } finally {
    proxy.$loader.setAppLoading(false);
  }
}

// ER-11818 — N > 0 opens the confirmation (with the count); N = 0 proceeds directly;
// an undeterminable preview (null) falls back to the pre-existing confirmation dialog.
async function openActivationConfirmation(kind) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const count = await fetchResponsibilityActivationCount(
    kind,
    accountId,
    computedResponsibilityId.value
  );
  activationEmployeeCount.value = count ?? 0;
  if (count === 0) {
    await (kind === "activate" ? handleActivateResponsibility() : handleReActivateResponsibility());
    return;
  }
  if (kind === "activate") showActivateDialog.value = true;
  else showReActivateDialog.value = true;
}

async function handleActivateResponsibility() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    proxy.$loader.setAppLoading(true);
    await updateActivateResponsibility(accountId, resId);
    showActivateDialog.value = false;
    await refreshResponsibilityDetails(accountId, resId);
    emit("responsibilityDataUpdated");
  } catch (error) {
    console.error("Error activating responsibility:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
}

async function handleRetireResponsibility() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    proxy.$loader.setAppLoading(true);
    await updateRetireResponsibility(accountId, resId, {
      status: "retired",
      name: responsibilityDetails.value?.name,
      account: accountId,
    });
    showRetireDialog.value = false;
    await refreshResponsibilityDetails(accountId, resId);
    emit("responsibilityDataUpdated");
  } catch (error) {
    console.error("Error retiring responsibility:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
}

async function handleReActivateResponsibility() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    proxy.$loader.setAppLoading(true);
    await updateReActivateResponsibility(accountId, resId, {
      status: "active",
      name: responsibilityDetails.value?.name,
      account: accountId,
    });
    showReActivateDialog.value = false;
    await refreshResponsibilityDetails(accountId, resId);
    emit("responsibilityDataUpdated");
  } catch (error) {
    console.error("Error re-activating responsibility:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
}

const unarchiveResponsibility = useUnarchiveOrgObject("responsibility", {
  getAccountId: () => JSON.parse(localStorage.getItem("currentAccountID")),
  openConfirm: () => {
    showArchiveDialog.value = true;
  },
  closeConfirm: () => {
    showArchiveDialog.value = false;
  },
  onUnarchived: async ({ id }) => {
    try {
      await refreshResponsibilityDetails(JSON.parse(localStorage.getItem("currentAccountID")), id);
      emit("responsibilityDataUpdated");
    } catch (error) {
      console.error("Error refreshing responsibility after unarchive:", error);
    }
  },
  setLoading: (value) => proxy.$loader.setAppLoading(value),
});
const {
  target: unarchiveTarget,
  conflictOpen: unarchiveConflictOpen,
  busy: unarchiveBusy,
  errorMessage: unarchiveError,
  nameError: unarchiveNameError,
} = unarchiveResponsibility;

function onArchiveDialogClose() {
  showArchiveDialog.value = false;
  unarchiveResponsibility.close();
}

async function handleArchiveResponsibility() {
  if (archiveAction.value === "unarchive") {
    await unarchiveResponsibility.confirm();
    return;
  }
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    proxy.$loader.setAppLoading(true);
    await updateArchiveResponsibility(accountId, "responsibility", resId);
    showArchiveDialog.value = false;
    await refreshResponsibilityDetails(accountId, resId);
    emit("responsibilityDataUpdated");
  } catch (error) {
    console.error("Error archiving/unarchiving responsibility:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
}

async function loadAttachableRoles(page = 1, searchTerm = "", isLoadMore = false) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;
  const limit = 10;
  const offset = (page - 1) * limit;

  try {
    if (!isLoadMore) {
      attachableRoles.value = [];
    }
    isLoadingMore.value = true;

    const response = await fetchAttachableRoles(accountId, resId, limit, offset, searchTerm);

    if (response?.data?.results) {
      const newItems = response.data.results.map((role) => ({
        id: role.id,
        code: role.code_str?.match(/\d+/)?.[0] || role.code,
        name: role.name,
        status: role.status,
        attached: role?.attached_to_responsibility,
      }));

      if (isLoadMore && attachableRoles.value) {
        attachableRoles.value = [...attachableRoles.value, ...newItems];
      } else {
        attachableRoles.value = newItems;
      }

      currentPage.value = page;
      totalCount.value = response.data.count || 0;
      hasMoreData.value = page * limit < totalCount.value;
    }
  } catch (error) {
    console.error("Error loading roles:", error);
  } finally {
    isLoadingMore.value = false;
  }
}

async function loadMoreRoles() {
  if (isLoadingMore.value || !hasMoreData.value) {
    return;
  }
  const nextPage = currentPage.value + 1;
  await loadAttachableRoles(nextPage, currentSearchTerm.value, true);
}

async function searchRoles(searchTerm) {
  currentSearchTerm.value = searchTerm;
  currentPage.value = 1;
  hasMoreData.value = true;
  await loadAttachableRoles(1, searchTerm, false);
}

function handleRoleSelected(role) {
  selectedRoleId.value = role?.id;
}

async function handleAddNewRole(item) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    proxy.$loader.show();
    const payload = {
      name: item.name,
      account: accountId,
    };
    // Create new role via store dispatch
    const newRole = await proxy.$store.dispatch("readinessCatalogsStore/createRole", {
      role: payload,
      accountId: accountId,
    });
    selectedRoleId.value = newRole.id;
    // Attach the responsibility to the newly created role
    await attachResponsibilityToRole(accountId, newRole.id, {
      attachment_id: resId,
    });
    showAttachRoleDialog.value = false;
    // Reset pagination state
    currentPage.value = 1;
    currentSearchTerm.value = "";
    selectedRoleId.value = null;
    await refreshResponsibilityDetails(accountId, resId);
  } catch (error) {
    console.error("Error creating and attaching to role:", error);
  } finally {
    proxy.$loader.hide();
  }
}

async function handleAttachToRole() {
  if (!selectedRoleId.value) return;

  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const resId = computedResponsibilityId.value;

  try {
    isAssigningRole.value = true;
    proxy.$loader.setAppLoading(true);
    // Attach responsibility to the selected role
    await attachResponsibilityToRole(accountId, selectedRoleId.value, {
      attachment_id: resId,
    });
    showAttachRoleDialog.value = false;
    // Reset state
    selectedRoleId.value = null;
    currentPage.value = 1;
    currentSearchTerm.value = "";
    await refreshResponsibilityDetails(accountId, resId);
    emit("responsibilityDataUpdated");
  } catch (error) {
    console.error("Error attaching responsibility to role:", error);
  } finally {
    isAssigningRole.value = false;
    proxy.$loader.setAppLoading(false);
  }
}
</script>

<style scoped>
.responsibility-card-content {
  width: 100%;
  margin-top: 24px;
}
.responsibility-card-wrapper {
  background: #fff;
  border-radius: 8px;
  box-shadow:
    0px 1px 2px 0px #1018280f,
    0px 1px 3px 0px #1018281a;
  border: 1px solid #eaecf0;
  width: 100%;
}
.responsibility-card-header-wrapper {
  padding: 24px 0 0 0;
}
.responsibility-card-menu {
  margin-top: 12px;
}
.inspect-form-page-wrapper {
  width: 100%;
}
</style>
